import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Checkbox, DatePicker, Input, message, Modal, Space, Table, Tabs, Typography } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { BorderOutlined, CheckSquareOutlined, DownloadOutlined } from '@ant-design/icons'
import { Cell, downloadExport, ExportBundle } from '../../utils/exportPatientHistory'

type Props = { bundle: ExportBundle | null; onClose: () => void }

const ExportPreviewModal: React.FC<Props> = ({ bundle, onClose }) => {
  const [downloading, setDownloading] = useState(false)
  const [excluded, setExcluded] = useState<Record<string, Set<number>>>({})
  const [excludedColumns, setExcludedColumns] = useState<Record<string, Set<number>>>({})
  const [excludedSheets, setExcludedSheets] = useState<Set<string>>(new Set())
  const [query, setQuery] = useState('')
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([null, null])

  useEffect(() => {
    setRange([null, null])
    setExcluded({})
    setExcludedColumns({})
    setExcludedSheets(new Set())
    setQuery('')
  }, [bundle])

  const isSheetSelected = (sheet: string) => !excludedSheets.has(sheet)
  const isColumnSelected = (sheet: string, col: number) => !excludedColumns[sheet]?.has(col)
  const isSelected = (sheet: string, index: number) => !excluded[sheet]?.has(index)

  const inRange = useMemo(() => {
    const [start, end] = range
    const startTime = start ? start.startOf('day').valueOf() : null
    const endTime = end ? end.endOf('day').valueOf() : null
    return Object.fromEntries(
      (bundle?.sheets ?? []).map((sheet) => {
        const dateCol = sheet.headers.indexOf('Date')
        return [
          sheet.name,
          sheet.rows.map((row) => {
            if (startTime === null && endTime === null) return true
            const time = dayjs(String(row[dateCol] ?? '')).valueOf()
            if (Number.isNaN(time)) return false
            return (startTime === null || time >= startTime) && (endTime === null || time <= endTime)
          }),
        ]
      })
    ) as Record<string, boolean[]>
  }, [bundle, range])

  const isIncluded = (sheet: string, index: number) => isSelected(sheet, index) && inRange[sheet]?.[index] !== false

  const toggleSheet = (sheet: string, selected: boolean) =>
    setExcludedSheets((previous) => {
      const next = new Set(previous)
      if (selected) next.delete(sheet)
      else next.add(sheet)
      return next
    })

  const toggleColumn = (sheet: string, col: number, selected: boolean) =>
    setExcludedColumns((previous) => {
      const next = new Set(previous[sheet])
      if (selected) next.delete(col)
      else next.add(col)
      return { ...previous, [sheet]: next }
    })

  const setSelection = (sheet: string, indices: number[], selected: boolean) =>
    setExcluded((previous) => {
      const next = new Set(previous[sheet])
      indices.forEach((i) => (selected ? next.delete(i) : next.add(i)))
      return { ...previous, [sheet]: next }
    })

  const matches = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    return Object.fromEntries(
      (bundle?.sheets ?? []).map((sheet) => [
        sheet.name,
        sheet.rows
          .map((_, i) => i)
          .filter((i) => inRange[sheet.name]?.[i] !== false)
          .filter(
            (i) =>
              !keyword || sheet.rows[i].some((cell) => cell !== null && String(cell).toLowerCase().includes(keyword))
          ),
      ])
    ) as Record<string, number[]>
  }, [bundle, query, inRange])

  const searching = !!query.trim()
  const matchCount = Object.values(matches).reduce((total, indices) => total + indices.length, 0)

  const selectedCount = (bundle?.sheets ?? [])
    .filter((sheet) => isSheetSelected(sheet.name))
    .reduce((total, sheet) => total + sheet.rows.filter((_, i) => isIncluded(sheet.name, i)).length, 0)

  const setAll = (selected: boolean) =>
    Object.entries(matches).forEach(([sheet, indices]) => setSelection(sheet, indices, selected))

  const download = async () => {
    if (!bundle) return
    setDownloading(true)
    try {
      await downloadExport({
        ...bundle,
        sheets: bundle.sheets
          .filter((sheet) => isSheetSelected(sheet.name))
          .map((sheet) => {
            const keep = (_: unknown, col: number) => isColumnSelected(sheet.name, col)
            return {
              ...sheet,
              headers: sheet.headers.filter(keep),
              widths: sheet.widths.filter(keep),
              rows: sheet.rows.filter((_, i) => isIncluded(sheet.name, i)).map((row) => row.filter(keep)),
            }
          })
          .filter((sheet) => sheet.rows.length > 0),
      })
      onClose()
    } catch (err) {
      console.error(err)
      message.error('Failed to export. Please try again later.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Modal
      open={!!bundle}
      title='Export Preview'
      width='90vw'
      style={{ top: 24 }}
      onCancel={onClose}
      keyboard={false}
      maskClosable={false}
      destroyOnClose
      footer={[
        <Button key='cancel' onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key='download'
          type='primary'
          icon={<DownloadOutlined />}
          loading={downloading}
          disabled={selectedCount === 0}
          onClick={download}>
          Download
        </Button>,
      ]}>
      {bundle && (
        <>
          <Typography.Text type='secondary'>
            {bundle.fileName} · Untick any test, row or column you don&apos;t want in the file. Only records within the
            date range are downloaded.
          </Typography.Text>
          <Space wrap style={{ display: 'flex', marginTop: 12 }}>
            <Input.Search
              allowClear
              placeholder='Search name, UID, account, date or any value'
              style={{ width: 480 }}
              onChange={(e) => setQuery(e.target.value)}
              onSearch={setQuery}
            />
            <DatePicker.RangePicker
              allowEmpty={[true, true]}
              placeholder={['From (any)', 'To (any)']}
              value={range}
              onChange={(value) => setRange([value?.[0] ?? null, value?.[1] ?? null])}
            />
            <Button icon={<CheckSquareOutlined />} disabled={searching && !matchCount} onClick={() => setAll(true)}>
              {searching ? 'Select all matches' : 'Select all'}
            </Button>
            <Button icon={<BorderOutlined />} disabled={searching && !matchCount} onClick={() => setAll(false)}>
              {searching ? 'Unselect all matches' : 'Unselect all'}
            </Button>
            {searching && <Typography.Text type='secondary'>{matchCount} matching rows</Typography.Text>}
          </Space>
          <Tabs
            items={bundle.sheets.map((sheet) => {
              const visible = matches[sheet.name] ?? []
              const sheetSelected = isSheetSelected(sheet.name)
              const inRangeCount = sheet.rows.filter((_, i) => inRange[sheet.name]?.[i] !== false).length
              const selectedInSheet = sheet.rows.filter((_, i) => isIncluded(sheet.name, i)).length
              const selectedVisible = visible.filter((i) => isSelected(sheet.name, i)).length
              return {
                key: sheet.name,
                label: (
                  <span style={{ opacity: sheetSelected ? 1 : 0.45 }}>
                    <Checkbox
                      checked={sheetSelected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggleSheet(sheet.name, e.target.checked)}
                      style={{ marginRight: 6 }}
                    />
                    {searching
                      ? `${sheet.name} (${visible.length} found)`
                      : `${sheet.name} (${selectedInSheet}/${inRangeCount})`}
                  </span>
                ),
                children: (
                  <>
                    {!sheetSelected && (
                      <Alert
                        type='warning'
                        showIcon
                        style={{ marginBottom: 12, padding: '6px 12px' }}
                        message="Not included in the file. Tick this test's tab to include it."
                      />
                    )}
                    <div style={{ opacity: sheetSelected ? 1 : 0.45 }}>
                      <Table<number>
                        size='small'
                        bordered
                        rowKey={(i) => i}
                        dataSource={visible}
                        rowSelection={{
                          columnTitle: (
                            <Checkbox
                              checked={visible.length > 0 && selectedVisible === visible.length}
                              indeterminate={selectedVisible > 0 && selectedVisible < visible.length}
                              disabled={!visible.length}
                              onChange={(e) => setSelection(sheet.name, visible, e.target.checked)}
                            />
                          ),
                          selectedRowKeys: visible.filter((i) => isSelected(sheet.name, i)),
                          onSelect: (i, selected) => setSelection(sheet.name, [i], selected),
                        }}
                        columns={sheet.headers.map((header, col) => {
                          const selected = isColumnSelected(sheet.name, col)
                          const dimmed = selected ? {} : { style: { opacity: 0.35 } }
                          return {
                            title: (
                              <Checkbox
                                checked={selected}
                                onChange={(e) => toggleColumn(sheet.name, col, e.target.checked)}>
                                {header}
                              </Checkbox>
                            ),
                            key: header,
                            width: sheet.widths[col] * 8 + 24,
                            ellipsis: true,
                            onHeaderCell: () => dimmed,
                            onCell: () => dimmed,
                            render: (_: unknown, i: number) => (sheet.rows[i][col] ?? '') as Cell,
                          }
                        })}
                        scroll={{ x: 'max-content', y: '50vh' }}
                        pagination={{ pageSize: 50, showSizeChanger: false }}
                        locale={{ emptyText: searching ? 'No matching records' : 'No records' }}
                      />
                    </div>
                  </>
                ),
              }
            })}
          />
        </>
      )}
    </Modal>
  )
}

export default ExportPreviewModal
